import
{
    PaginatedResult,
    PaginationRequest,
    permissionAllowed,
    User
} from "@ews/react-data";

import { useL10n } from "@ews/react-localization-context";
import { IonButton, IonCol, IonIcon } from "@ionic/react";
import { checkmark, closeOutline, } from "ionicons/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Locale } from "../../setup/locale";
import FilterList, { isFilterActive } from "../FilterList/FilterList";
import { FilterState } from "../FilterList/types";
import List from "../List/List";
import { defaultPageRange } from "../List/Pagination/pagerange";
import { HeaderOption } from "../List/types";
import { useMyDateFormat, useMyPermissions } from "../Login/LoginProvider";
import Sidebar from "../Navigation/Sidebar/User";
import { usePersistentState } from "../Persistence";
import { useInvokeLogoutUser } from "../ReactData/rpc";
import { useFetchUserList } from "../ReactData/user";
import SettingsTextInput from "../Settings/SettingsComponents/SettingsTextInput/SettingsTextInput";
import { Filter } from "../System/Reports/types/reportTypes";
import { UsersListProps } from "./type";
import listStyle from "./UserList.module.scss";


type ListFilter = {
    'details.lastName'?: string;
    email?: string;
    username?: string;
    searchOperator?: "OR";
};

const standardFilter: Filter<ListFilter> = {
    sortCriteria: "username",
    sortOrder: "ASC",
    //searchOperator: "OR"
    pageSize: defaultPageRange,
};

type PersistentFilter = Filter<ListFilter & { customerId?: string; }> & FilterState;
const ignoreKeys: (keyof PersistentFilter)[] = ["pageSize", "isFilterOpen", "page", "pageSize", "sortCriteria", "sortOrder"];

const link = { url: "user", paramsKey: "id" };

const onMenu = (id: string, onMenuToggle: () => void, onSimulate: (() => void) | null) =>
    <div style={{ height: "215px" }}>
        <Sidebar onMenuToggle={onMenuToggle} onSimulate={onSimulate || (() => { })} routeParams={{ id }} />
    </div>;
const UserList: React.FC<UsersListProps> = ({
    customerId,
    filter,
    onIsOpen,
    onFilterActive
}) =>
{
    const filterDebounceTime = 900;
    const dateFormat = useMyDateFormat();

    const header: HeaderOption<Locale, User>[] = useMemo(() => [
        {
            title: 'COM_FACPMANAGEMENT_USERS_USERID',
            key: 'username',
            className: listStyle.username,
            both: true,
        },
        {
            title: 'COM_FACPMANAGEMENT_USERS_FIRSTNAME',
            key: 'firstName',
            className: listStyle.firstName,
            both: true,
            subEntity: 'details.firstName'
        },
        {
            title: 'COM_FACPMANAGEMENT_USERS_LASTNAME',
            key: 'lastName',
            className: listStyle.lastName,
            both: true,
            subEntity: 'details.lastName'
        },
        {
            title: 'COM_FACPMANAGEMENT_USERS_CUSTOMERID',
            key: 'customerNumber',
            className: listStyle.customerNumber,
            breakpoint: 992, up: true,
            subEntity: 'customer.customerNumber',
            option: { noSort: true }
        },
        {
            title: 'COM_FACPMANAGEMENT_CUSTOMERS_NAME',
            key: 'customerName',
            className: listStyle.customerName,
            breakpoint: 1162, up: true,
            subEntity: 'customer.name',
            option: { noSort: true }
        },
        {
            title: 'COM_FACPMANAGEMENT_USERS_LAST_VISIT_DATE',
            key: 'lastLogin',
            className: listStyle.lastLogin,
            breakpoint: 1362, up: true,
            child: (row) => <>{row.lastLogin ? dateFormat(new Date(row.lastLogin)) : ""}</>
        },
        {
            title: 'COM_CONTACT_EMAIL_LABEL',
            key: 'email',
            className: listStyle.email,
            both: true,
        },
        {
            title: 'COM_BANNERS_HEADING_ACTIVE',
            key: 'active',
            className: listStyle.active,
            both: true,
            child: (row) =>
            {
                return row.active ? <IonIcon size="large" color="success" icon={checkmark} /> : <IonIcon size="large" color="danger" icon={closeOutline} />;
            }
        },
    ], [dateFormat]);



    const myPermissions = useMyPermissions();
    const allowedSettingsLink = permissionAllowed(myPermissions.createUser || 0);

    const [result, setResult] = useState<PaginatedResult<User>>({
        currentPage: 0,
        pageSize: standardFilter.pageSize || defaultPageRange,
        totalCountOfEntries: 0,
        totalCountOfPages: 0,
        results: [],
    });
    const fetchUsers = useFetchUserList();

    const [currentState, setState] = usePersistentState<PersistentFilter>('users:filter', standardFilter);

    const sortCriteria = useRef<{
        key: string;
        direction: "ASC" | "DESC";
    } | undefined>(undefined);

    useEffect(() =>
    {
        if (customerId && currentState.customerId !== customerId) {
            currentState.customerId = customerId;
        }
    }, [currentState, customerId]);

    useEffect(() =>
    {

        async function handleFilterResponse(filterCriteria: PaginationRequest & ListFilter)
        {
            const response: PaginatedResult<User> = await fetchUsers(filterCriteria) as PaginatedResult<User>;

            setResult(response);
        }

        handleFilterResponse(currentState);
    }, [currentState, fetchUsers]);

    const onPageChange = useMemo(() => (page: PersistentFilter["page"]) => setState((priv) => ({ ...priv, page })), [setState]);

    const onSortChange = useMemo(() => (sortCriteria: PersistentFilter["sortCriteria"], sortOrder: PersistentFilter["sortOrder"]) => setState((priv) => ({ ...priv, sortCriteria, sortOrder })), [setState]);
    const onPageSizeChange = useMemo(() => (pageSize: PersistentFilter["pageSize"]) => setState((priv) => ({ ...priv, pageSize })), [setState]);
    const onReloadChange = useMemo(() => () => setState((priv) => ({ ...priv })), [setState]);

    const onIsFilterOpen = useCallback((state: boolean) => { onIsOpen && onIsOpen(state, "main"); }, [onIsOpen]);
    const onIsDetailFilterOpen = useCallback((isDetailOpen: boolean) => onIsOpen && onIsOpen(isDetailOpen, "detail"), [onIsOpen]);
    const onResetFilter = useCallback(() => setState(() => standardFilter), [setState]);

    const isFilterChanged = isFilterActive<PersistentFilter>(currentState, standardFilter, ignoreKeys);

    useEffect(() =>
    {
        if (filter?.isFilterActive !== isFilterChanged) {
            onFilterActive && onFilterActive(isFilterChanged);
        }
    }, [isFilterChanged, filter, onFilterActive]);

    useEffect(() =>
    {
        sortCriteria.current =
            currentState.sortCriteria ? {
                key: currentState.sortCriteria,
                direction: currentState.sortOrder || "ASC"
            } : undefined;

    }, [currentState.sortCriteria, currentState.sortOrder]);

    return <div className={`${listStyle.container}`}>
        <FilterList
            isOpen={filter?.isFilterOpen || false}
            isDetailsOpen={filter?.isDetailOpen || false}
            onIsOpen={onIsFilterOpen}
            onDetailOpen={onIsDetailFilterOpen}
            onReset={onResetFilter}>
            <IonCol>

                <SettingsTextInput
                    id="lastname"
                    text="COM_FACPMANAGEMENT_USERS_LASTNAME"
                    debounce={filterDebounceTime}
                    value={currentState["details.lastName"]}

                    onChange={(username) =>
                    {
                        const currentFilter = { ...currentState };

                        if (username.length) currentFilter["details.lastName"] = username;
                        else delete currentFilter["details.lastName"];

                        setState(currentFilter);
                    }}
                />

            </IonCol>
            <IonCol>
                <SettingsTextInput
                    id="email"
                    text="COM_CONTACT_EMAIL_LABEL"
                    debounce={filterDebounceTime}
                    value={currentState.email}
                    onChange={(email) =>
                    {
                        const currentFilter = { ...currentState };

                        if (email.length) currentFilter.email = email;
                        else delete currentFilter.email;

                        setState(currentFilter);
                    }}
                />


            </IonCol>
            <IonCol>
                <SettingsTextInput
                    id="username"
                    text="COM_FACPMANAGEMENT_USERS_USERID"
                    debounce={filterDebounceTime}
                    value={currentState.username}
                    onChange={(value) =>
                    {
                        const currentFilter = { ...currentState };

                        if (value.length) currentFilter.username = value;
                        else delete currentFilter.username;

                        setState(currentFilter);
                    }}
                />


            </IonCol>
            <IonCol></IonCol>
        </FilterList >

        <List
            rows={result.results}
            rowClass={listStyle.row}
            sort={sortCriteria.current}
            onSort={onSortChange}
            onPageChange={onPageChange}
            onPageSize={onPageSizeChange}
            onReload={onReloadChange}
            pageSize={result.pageSize}
            currentPage={result.currentPage}
            numberOfPages={result.totalCountOfPages}
            /* maxHeight={mobileView ? undefined : '50vh'} */
            noLink={!allowedSettingsLink}
            link={link}
            onMenu={onMenu}
            headers={header}

        />

    </div>;
};

export default UserList;
